/**
 * 视频封面 URL 工具函数
 * 统一处理封面 URL 的获取和 fallback 逻辑
 */

import { API_BASE_URL } from '../config/api';
import { resolveAssetUrl } from './assetUrl';
import type { VideoDescriptor } from '../types/api';

/**
 * 默认封面图片路径（相对于 public 目录）
 * 优先使用 SVG（矢量图，清晰），如果不存在则使用 JPG
 */
export const DEFAULT_COVER_PATH = '/default-cover.svg';

/**
 * 获取视频封面 URL（带 fallback）
 * 
 * @param video - 视频描述符（VideoDescriptor）
 * @returns 封面图片 URL，如果 coverUrl 为空则返回默认封面
 */
export function getVideoCoverUrl(video: VideoDescriptor): string {
  // 如果 coverUrl 存在，使用实际封面（使用 resolveAssetUrl 统一解析）
  if (video.coverUrl) {
    const resolvedUrl = resolveAssetUrl(video.coverUrl);
    return resolvedUrl || DEFAULT_COVER_PATH;
  }
  
  // 否则使用默认封面
  return DEFAULT_COVER_PATH;
}

/**
 * 获取历史记录项的封面 URL（带 fallback）
 * 
 * @param coverUrl - 封面 URL（可选）
 * @param videoId - 视频 ID（用于回退到旧接口）
 * @returns 封面图片 URL
 */
export function getHistoryCoverUrl(coverUrl?: string, videoId?: string): string {
  // 如果 coverUrl 存在，使用实际封面（使用 resolveAssetUrl 统一解析）
  if (coverUrl) {
    const resolvedUrl = resolveAssetUrl(coverUrl);
    return resolvedUrl || DEFAULT_COVER_PATH;
  }
  
  // 如果有 videoId，尝试使用旧的缩略图接口
  if (videoId) {
    return `${API_BASE_URL}/video-thumbnail?videoId=${encodeURIComponent(videoId)}`;
  }
  
  // 否则使用默认封面
  return DEFAULT_COVER_PATH;
}

/**
 * 处理封面图片加载失败的回调
 * 当封面加载失败时，替换为默认封面
 * 
 * @param e - 图片加载错误事件
 */
export function handleCoverError(e: React.SyntheticEvent<HTMLImageElement, Event>): void {
  const target = e.target as HTMLImageElement;
  
  // 如果当前不是默认封面，则替换为默认封面
  if (!target.src.includes('default-cover.svg') && !target.src.includes('default-cover.jpg')) {
    console.log(`[VideoCover] 封面加载失败，替换为默认封面: 原URL=${target.src}`);
    target.src = DEFAULT_COVER_PATH;
  } else {
    // 如果默认封面也加载失败，隐藏图片显示占位符
    console.error(`[VideoCover] 默认封面也加载失败: ${target.src}`);
    target.style.display = 'none';
    const placeholder = target.parentElement?.querySelector('.thumbnail-placeholder');
    if (placeholder) {
      (placeholder as HTMLElement).style.display = 'flex';
    }
  }
}

